import { RefObject, useEffect } from 'react';

import {
  CellEditingStartedEvent,
  CellPosition,
  CellValueChangedEvent,
} from 'ag-grid-community';
import { FirstDataRenderedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { FreshShipmentActions } from '@/app/slices/freshShipment';
import { RootState } from '@/app/store';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultFreshShipmentColDef } from '@/grid/defaults';
import { TOA_GetFreshShipmentFertList } from '@/rtk-query.gql';
import { useUpdateFertTransaction } from '@/services/fresh-shipment/hooks';
import { getLocalStorage, setLocalStorage } from '@/utils/storage';

import { ivfViewGridColDefs } from '../utils/columnDefs';
import { gridOptions, rowClassRules } from '../utils/constants';
import { FreshShipmentFertList } from '../utils/types';

type FreshShipmentIvfViewGridProps = {
  className: string;
  themeMode: string;
  fertilizations: TOA_GetFreshShipmentFertList[] | undefined;
  me: string;
  quickSearchValue: string;
  handleUpdateFertilization: (params: CellValueChangedEvent) => void;
  handleCellEditingStarted: (params: CellEditingStartedEvent) => void;
  gridRef: RefObject<AgGridReact<any>>;
  onFirstDataRendered: (params: FirstDataRenderedEvent) => void;
};

export const FreshShipmentIvfViewGrid = ({
  className,
  themeMode,
  fertilizations,
  me,
  quickSearchValue,
  handleUpdateFertilization,
  handleCellEditingStarted,
  gridRef,
  onFirstDataRendered,
}: FreshShipmentIvfViewGridProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const ivfViewGridRef = gridRef;
  const predictionTechLoading = useSelector(
    (state: RootState) => state.freshShipment.predictionTechLoading,
  );

  const selectedRows = getLocalStorage(
    'freshShipment',
    'selectedFreshShipmentRows',
  );

  const [params] = useSearchParams();
  const d5 = params.get('d5');

  const { mutateAsync: updateFertilization } = useUpdateFertTransaction();

  useEffect(() => {
    if (ivfViewGridRef.current?.api) {
      if (selectedRows.length > 0) {
        ivfViewGridRef.current.api.forEachNode((node) => {
          if (selectedRows.includes(node.data.fertilizationId)) {
            node.setSelected(true);
          }
        });
      }
    }
  }, [quickSearchValue, selectedRows]);

  useEffect(() => {
    if (ivfViewGridRef.current?.api) {
      if (predictionTechLoading) {
        ivfViewGridRef.current.api.showLoadingOverlay();
      } else {
        ivfViewGridRef.current.api.hideOverlay();
      }
    }
  }, [predictionTechLoading]);

  const handleSelectionChanged = () => {
    const selectedRows = ivfViewGridRef.current?.api.getSelectedRows();
    setLocalStorage(
      'freshShipment',
      (selectedRows as FreshShipmentFertList[]).map(
        (node) => node.fertilizationId,
      ),
      'selectedFreshShipmentRows',
    );
    dispatch({
      type: FreshShipmentActions.SET_SELECTED_ROWS,
      selectedRows: (selectedRows as FreshShipmentFertList[]).map(
        (node) => node.fertilizationId,
      ),
    });
  };

  const handleCellKeyDown = (event: any) => {
    if (event.event.key === 'Enter') {
      const currentCell: CellPosition = event.api.getFocusedCell();
      if (currentCell) {
        const nextRowIndex = currentCell.rowIndex + 1;
        event.api.setFocusedCell(nextRowIndex, currentCell.column);
        event.api.clearRangeSelection();
      }
    }
  };

  return (
    <div className="fresh-shipment-form__grid">
      <WithLocalStorage storageKey={`fresh-shipments-ivf-grid`}>
        <AgGridReact
          ref={ivfViewGridRef}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}${className ? ` ${className}` : ''} with-vertical-lines`}
          rowData={fertilizations}
          getRowId={(params) => params.data.fertilizationId}
          defaultColDef={defaultFreshShipmentColDef}
          columnDefs={ivfViewGridColDefs}
          overlayLoadingTemplate={
            predictionTechLoading
              ? 'Loading...'
              : 'Please Select a Site and a Date'
          }
          rowSelection="multiple"
          rowClassRules={rowClassRules}
          onCellValueChanged={handleUpdateFertilization}
          onSelectionChanged={handleSelectionChanged}
          onCellEditingStarted={handleCellEditingStarted}
          onCellKeyDown={handleCellKeyDown}
          context={{ me, updateFertilization, d5 }}
          quickFilterText={quickSearchValue}
          gridOptions={{ ...defaultGridOptions, ...gridOptions }}
          suppressRowClickSelection={true}
          suppressRowHoverHighlight={true}
          onFirstDataRendered={onFirstDataRendered}
          getRowHeight={() => 30}
        />
      </WithLocalStorage>
    </div>
  );
};
